import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContextRedux'
import { useToast } from '../contexts/ToastContext'
import { getVersionInfo } from '../version.js'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuth()
  const { addToast } = useToast()

  // Log version info on login page load
  useEffect(() => {
    const versionInfo = getVersionInfo()
    console.log(`🔐 Login page loaded - ${versionInfo.client_name} v${versionInfo.version}`)
    console.log(`   Environment: ${versionInfo.environment} | API: ${versionInfo.api_url}`)
  }, [])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/menu'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      addToast('Please enter both email and password', 'warning')
      return
    }
    
    setLoading(true)

    const result = await login(email, password)
    
    if (result.success) {
      addToast('Login successful!', 'success')
      // Navigate to the page they were trying to access, or default to chat
      const from = location.state?.from?.pathname || '/menu'
      navigate(from, { replace: true })
    } else {
      addToast(result.error || 'Login failed', 'error')
    }
    
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="card w-96 bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-2xl font-bold text-center mb-6">
            Agentic Research Login
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Email</span>
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-lg bg-base-200 focus:outline-none transition-none"
                style={{ 
                  border: 'none !important', 
                  outline: 'none !important', 
                  backgroundColor: 'rgb(242, 242, 242)',
                  boxShadow: 'none !important',
                  WebkitAppearance: 'none'
                }}
                onFocus={(e) => { 
                  e.target.style.backgroundColor = 'rgb(242, 242, 242)'
                  e.target.style.border = 'none'
                  e.target.style.boxShadow = 'none'
                }}
                onBlur={(e) => { 
                  e.target.style.backgroundColor = 'rgb(242, 242, 242)'
                  e.target.style.border = 'none'
                  e.target.style.boxShadow = 'none'
                }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-control w-full mt-4">
              <label className="label">
                <span className="label-text">Password</span>
              </label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-lg bg-base-200 focus:outline-none transition-none"
                style={{ 
                  border: 'none !important', 
                  outline: 'none !important', 
                  backgroundColor: 'rgb(242, 242, 242)',
                  boxShadow: 'none !important',
                  WebkitAppearance: 'none'
                }}
                onFocus={(e) => { 
                  e.target.style.backgroundColor = 'rgb(242, 242, 242)'
                  e.target.style.border = 'none'
                  e.target.style.boxShadow = 'none'
                }}
                onBlur={(e) => { 
                  e.target.style.backgroundColor = 'rgb(242, 242, 242)'
                  e.target.style.border = 'none'
                  e.target.style.boxShadow = 'none'
                }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            <div className="form-control mt-6">
              <button 
                type="submit" 
                className={`w-full py-3 rounded-lg bg-primary text-primary-content font-medium hover:bg-primary-focus transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ 
                  border: 'none !important', 
                  outline: 'none !important', 
                  boxShadow: 'none !important',
                  WebkitAppearance: 'none'
                }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="text-center mt-4">
            <p className="text-sm text-base-content/60">
              Your session will remain active for 90 days
            </p>
          </div>

          {/* Dev mode notice */}
          {import.meta.env.DEV && (
            <div className="alert alert-info mt-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span className="text-sm">
                Development mode: Use any email/password
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}